import React, { useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { Menu, X, Phone } from "lucide-react";
import { SERVICES } from "@/lib/services";
import AccountChoiceDialog from "@/components/AccountChoiceDialog";

const NAV_LINKS = [
    { to: "/insurance-solutions", label: "Solutions" },
    { to: "/leadership", label: "About" },
    { to: "/insurance-education-videos", label: "Education" },
    { to: "/blog", label: "Blog" },
    // Special token — intercepts in PageHeader to open the AccountChoiceDialog
    { to: "__account__", label: "Account" },
    { to: "/contact", label: "Contact" },
];

export default function PageHeader() {
    const [open, setOpen] = useState(false);
    const [solutionsOpen, setSolutionsOpen] = useState(false);
    const [accountDialogOpen, setAccountDialogOpen] = useState(false);
    const location = useLocation();

    const openAccountDialog = (e) => {
        if (e && typeof e.preventDefault === "function") e.preventDefault();
        setOpen(false);
        setAccountDialogOpen(true);
    };

    // Close menus when navigating to a new route. useState setters are stable
    // across renders by React's contract, so including them in the deps array
    // does not cause extra runs — it just satisfies the exhaustive-deps lint.
    React.useEffect(() => {
        setOpen(false);
        setSolutionsOpen(false);
    }, [location.pathname]);

    return (
        <header
            data-testid="page-header"
            className="relative z-30 flex w-full items-center justify-between border-b border-[rgba(212,175,55,0.12)] bg-brand-bg/70 px-6 py-4 backdrop-blur md:px-10 md:py-6"
        >
            <Link
                to="/"
                data-testid="header-brand-link"
                className="flex flex-col leading-none focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-gold focus-visible:ring-offset-2 focus-visible:ring-offset-brand-bg rounded-sm"
            >
                <span className="font-serif text-xl tracking-wide text-brand-ivory md:text-2xl">
                    Endurance Casualty
                </span>
                <span className="mt-1 text-[10px] uppercase tracking-[0.4em] text-brand-goldSoft md:text-[11px]">
                    Insurance Agency
                </span>
            </Link>

            {/* Desktop nav */}
            <nav
                aria-label="Primary"
                className="hidden items-center gap-2 md:flex"
            >
                <div
                    onMouseEnter={() => setSolutionsOpen(true)}
                    onMouseLeave={() => setSolutionsOpen(false)}
                    className="relative"
                >
                    <Link
                        to="/insurance-solutions"
                        data-testid="header-link-solutions"
                        className="rounded-md px-3 py-2 text-sm uppercase tracking-[0.2em] text-brand-ivory hover:text-brand-gold2 focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-gold"
                    >
                        Solutions
                    </Link>
                    {solutionsOpen && (
                        <div
                            data-testid="header-solutions-dropdown"
                            className="absolute right-0 top-full mt-2 w-72 overflow-hidden rounded-xl border border-[rgba(212,175,55,0.25)] bg-[#0b0e13] shadow-[0_30px_60px_-20px_rgba(0,0,0,0.85)]"
                        >
                            <ul className="py-2">
                                {SERVICES.map((s) => (
                                    <li key={s.slug}>
                                        <Link
                                            to={s.path}
                                            data-testid={`header-link-${s.slug}`}
                                            className="flex flex-col gap-0.5 px-4 py-2.5 text-left transition-colors hover:bg-[rgba(212,175,55,0.06)] focus:outline-none focus-visible:bg-[rgba(212,175,55,0.06)]"
                                        >
                                            <span className="text-sm text-brand-ivory">
                                                {s.title}
                                            </span>
                                            <span className="text-[10px] uppercase tracking-[0.25em] text-brand-goldSoft">
                                                {s.kicker}
                                            </span>
                                        </Link>
                                    </li>
                                ))}
                            </ul>
                        </div>
                    )}
                </div>
                <Link
                    to="/insurance-education-videos"
                    data-testid="header-link-videos"
                    className="whitespace-nowrap rounded-md px-3 py-2 text-sm uppercase tracking-[0.2em] text-brand-ivory hover:text-brand-gold2 focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-gold"
                >
                    Education
                </Link>
                <Link
                    to="/blog"
                    data-testid="header-link-blog"
                    className="whitespace-nowrap rounded-md px-3 py-2 text-sm uppercase tracking-[0.2em] text-brand-ivory hover:text-brand-gold2 focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-gold"
                >
                    Blog
                </Link>
                <Link
                    to="/leadership"
                    data-testid="header-link-leadership"
                    className="whitespace-nowrap rounded-md px-3 py-2 text-sm uppercase tracking-[0.2em] text-brand-ivory hover:text-brand-gold2 focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-gold"
                >
                    About
                </Link>
                <button
                    type="button"
                    onClick={openAccountDialog}
                    data-testid="header-link-pay-premium"
                    className="whitespace-nowrap rounded-md px-3 py-2 text-sm uppercase tracking-[0.2em] text-brand-gold2 hover:text-brand-gold focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-gold"
                >
                    Account
                </button>
                <Link
                    to="/contact"
                    data-testid="header-link-contact"
                    className="whitespace-nowrap rounded-md px-3 py-2 text-sm uppercase tracking-[0.2em] text-brand-ivory hover:text-brand-gold2 focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-gold"
                >
                    Contact
                </Link>
                <a
                    data-testid="header-phone-link"
                    href="tel:+18559007405"
                    className="ml-2 inline-flex items-center gap-2 rounded-full border border-[rgba(212,175,55,0.35)] bg-[#0b0e13] px-4 py-2 text-sm font-medium text-brand-ivory transition-all hover:border-brand-gold hover:text-brand-gold2 focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-gold"
                >
                    <Phone aria-hidden="true" className="h-4 w-4 text-brand-gold" />
                    <span>855.900.7405</span>
                </a>
            </nav>

            {/* Mobile trigger */}
            <button
                type="button"
                aria-label={open ? "Close menu" : "Open menu"}
                aria-expanded={open}
                data-testid="header-mobile-toggle"
                onClick={() => setOpen(!open)}
                className="inline-flex h-10 w-10 items-center justify-center rounded-full border border-[rgba(212,175,55,0.35)] text-brand-ivory md:hidden focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-gold"
            >
                {open ? (
                    <X aria-hidden="true" className="h-5 w-5" />
                ) : (
                    <Menu aria-hidden="true" className="h-5 w-5" />
                )}
            </button>

            {/* Mobile drawer */}
            {open && (
                <div
                    data-testid="header-mobile-menu"
                    className="absolute left-0 right-0 top-full z-30 max-h-[calc(100vh-72px)] overflow-y-auto border-b border-[rgba(212,175,55,0.2)] bg-[#0b0e13] md:hidden"
                >
                    <ul className="px-6 py-4">
                        {NAV_LINKS.map((l) => (
                            <li key={l.to}>
                                {l.to === "__account__" ? (
                                    <button
                                        type="button"
                                        onClick={openAccountDialog}
                                        data-testid="mobile-link-account"
                                        className="block w-full py-2 text-left text-sm uppercase tracking-[0.2em] text-brand-gold2"
                                    >
                                        {l.label}
                                    </button>
                                ) : (
                                    <Link
                                        to={l.to}
                                        className="block py-2 text-sm uppercase tracking-[0.2em] text-brand-ivory"
                                    >
                                        {l.label}
                                    </Link>
                                )}
                            </li>
                        ))}
                        <li className="mt-2 border-t border-[rgba(212,175,55,0.15)] pt-2">
                            <p className="py-1 text-[10px] uppercase tracking-[0.3em] text-brand-goldSoft">
                                Insurance Topics
                            </p>
                            {SERVICES.map((s) => (
                                <Link
                                    key={s.slug}
                                    to={s.path}
                                    className="block py-1.5 text-sm text-brand-ivory/85"
                                >
                                    {s.title}
                                </Link>
                            ))}
                        </li>
                        <li className="mt-3">
                            <a
                                href="tel:+18559007405"
                                className="block rounded-full border border-brand-gold/60 px-4 py-2 text-center text-sm uppercase tracking-[0.2em] text-brand-gold2"
                            >
                                Call 855.900.7405
                            </a>
                        </li>
                    </ul>
                </div>
            )}

            {/* Account choice modal (intercepts header "Account" click) */}
            <AccountChoiceDialog
                open={accountDialogOpen}
                onOpenChange={setAccountDialogOpen}
            />
        </header>
    );
}
